import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Calendar, Image as ImageIcon, ListChecks, Palette, Plus, Trash2, Type, Upload, Users } from "lucide-react"

import TipTapEditor from "@crm-src/components/tiptap-editor"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { newsletterImagesApi } from "@crm-src/lib/newsletter-images-api"
import { useToast } from "@crm-src/hooks/use-toast"
import { generateSpotlightHtml, initials, safeColor, type SpotlightTenantInfo } from "./generate-html"
import { squareCropImageFile } from "./square-crop"
import {
  DEFAULT_SPOTLIGHT_COLORS,
  SPOTLIGHT_MAX_SPEAKERS,
  SOC_BANNER_URL,
  SPOTLIGHT_PALETTES,
  XOP_EMAIL_LOGO_URL,
  spotlightBannerOptions,
  toPreviewImageUrl,
  type SpotlightColors,
  type SpotlightContent,
  type SpotlightSection,
  type SpotlightSpeaker,
} from "./types"

interface SpotlightWebinarBuilderProps {
  tenantName: string
  tenantInfo?: SpotlightTenantInfo
  initialSections?: SpotlightSection[]
  onContentChange: (html: string) => void
  onSectionsChange?: (sections: SpotlightSection[]) => void
}

const newSpeakerId = () => `sp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`

export function defaultSpotlightContent(): SpotlightContent {
  return {
    preheader: "Join our live webinar — register today.",
    logoUrl: "",
    bannerUrl: SOC_BANNER_URL,
    eyebrow: "XOP Networks Webinar | September 30, 2026",
    title: "When Every Second Counts:",
    subtitle: "Mass Notification for Emergency Response",
    buttonLabel: "Register Now",
    registrationLink: "",
    greeting: "Hi {{first_name}},",
    intro:
      "<p>Introduce the problem your audience is facing, in two or three sentences.</p><p>Then say who is presenting and what attendees will walk away with.</p>",
    coverHeading: "During the webinar, we'll cover:",
    coverItems: [
      "The first topic you will cover",
      "The second topic you will cover",
      "The third topic you will cover",
      "A live demonstration of the solution",
    ],
    closingLine: "Discover what this means for your organisation – and how to get ahead of it.",
    whenHeading: "When?",
    when: "September 30, 2026 | 11:00 AM AST (8:00 AM GMT)",
    speakersHeading: "Meet Our Speakers",
    speakers: [{ id: newSpeakerId(), name: "Speaker Name", title: "Job Title,\nCompany", photoUrl: "" }],
    colors: { ...DEFAULT_SPOTLIGHT_COLORS },
  }
}

// Merge a saved section over the defaults so a field added later falls back
// sensibly on an older saved campaign instead of rendering `undefined`.
export function ensureSpotlightSection(saved: SpotlightSection[] | undefined): SpotlightSection {
  const savedContent = saved?.find((s) => s.type === "spotlight")?.content
  const base = defaultSpotlightContent()
  if (!savedContent) return { id: "spotlight", type: "spotlight", content: base }
  return {
    id: "spotlight",
    type: "spotlight",
    content: { ...base, ...savedContent, colors: { ...base.colors, ...(savedContent.colors || {}) } },
  }
}

const COLOR_FIELDS: ReadonlyArray<{ key: keyof SpotlightColors; label: string }> = [
  { key: "accent", label: "Accent (eyebrow, subtitle, headings)" },
  { key: "button", label: "Button" },
  { key: "buttonText", label: "Button text" },
  { key: "heading", label: "Title + names" },
  { key: "text", label: "Body text" },
  { key: "pageBackground", label: "Page background" },
  { key: "cardBackground", label: "Card background" },
]

function SectionTitle({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1rem", fontWeight: 600 }}>
      {icon}
      {children}
    </Box>
  )
}

export function SpotlightWebinarBuilder({
  tenantName,
  tenantInfo,
  initialSections,
  onContentChange,
  onSectionsChange,
}: SpotlightWebinarBuilderProps) {
  const [section, setSection] = useState<SpotlightSection>(() => ensureSpotlightSection(initialSections))
  const [customImages, setCustomImages] = useState<Array<{ id: number; url: string; name: string }>>([])
  const [uploading, setUploading] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Where the next picked file goes: "logo", "banner" or a speaker id.
  const uploadTargetRef = useRef<string>("")
  const { toast } = useToast()

  useEffect(() => {
    newsletterImagesApi
      .list()
      .then((images) =>
        setCustomImages(images.map((img, i) => ({ id: 5000 + i, url: img.url, name: img.name || "Custom Image" }))),
      )
      .catch((err) => console.error("Failed to load custom images:", err))
  }, [])

  // Re-hydrate when the parent supplies fresh initialSections (campaign switch).
  useEffect(() => {
    if (initialSections && initialSections.length > 0) {
      setSection(ensureSpotlightSection(initialSections))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSections])

  const lastSectionsJsonRef = useRef<string>("")
  const lastHtmlRef = useRef<string>("")

  useEffect(() => {
    const json = JSON.stringify([section])
    if (json === lastSectionsJsonRef.current) return
    lastSectionsJsonRef.current = json
    const html = generateSpotlightHtml(section, tenantInfo, new Date().getFullYear())
    if (html !== lastHtmlRef.current) {
      lastHtmlRef.current = html
      onContentChange(html)
    }
    onSectionsChange?.([section])
  }, [section, tenantInfo, onContentChange, onSectionsChange])

  const c = section.content
  const update = (patch: Partial<SpotlightContent>) =>
    setSection((prev) => ({ ...prev, content: { ...prev.content, ...patch } }))
  const updateColors = (patch: Partial<SpotlightColors>) =>
    setSection((prev) => ({ ...prev, content: { ...prev.content, colors: { ...prev.content.colors, ...patch } } }))
  // Functional update: a photo upload resolves AFTER an await, and must not
  // write back a speaker list captured before the operator's later edits.
  const updateSpeaker = (id: string, patch: Partial<SpotlightSpeaker>) =>
    setSection((prev) => ({
      ...prev,
      content: { ...prev.content, speakers: prev.content.speakers.map((s) => (s.id === id ? { ...s, ...patch } : s)) },
    }))

  const pickFile = (target: string) => {
    uploadTargetRef.current = target
    fileInputRef.current?.click()
  }

  const onFilePicked = async (file: File) => {
    const target = uploadTargetRef.current
    try {
      setUploading(target)
      // Headshots are squared before upload — the email can't crop them.
      const isSpeaker = target !== "logo" && target !== "banner"
      const toUpload = isSpeaker ? await squareCropImageFile(file) : file
      const uploaded = await newsletterImagesApi.upload(toUpload)
      if (target === "logo") update({ logoUrl: uploaded.url })
      else if (target === "banner") update({ bannerUrl: uploaded.url })
      else updateSpeaker(target, { photoUrl: uploaded.url })
      setCustomImages((prev) => [...prev, { id: 5000 + prev.length, url: uploaded.url, name: file.name }])
      toast({ title: "Success", description: "Image uploaded" })
    } catch (err) {
      console.error("Failed to upload image:", err)
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" })
    } finally {
      setUploading(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const uploadButton = (target: string, label: string) => (
    <Button
      variant="outlined"
      color="inherit"
      size="small"
      disabled={uploading !== null}
      onClick={() => pickFile(target)}
      startIcon={uploading === target ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
    >
      {uploading === target ? "Uploading…" : label}
    </Button>
  )

  const labelSx = { fontSize: 14, fontWeight: 500, mb: 0.5, display: "block" } as const
  const hintSx = { fontSize: 12, color: "text.secondary", mt: 0.5 } as const
  const plainText = (html: string) => (html || "").replace(/<[^>]*>/g, "")
  const accentPreview = safeColor(c.colors.accent, DEFAULT_SPOTLIGHT_COLORS.accent)

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="spotlight-builder">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void onFilePicked(file)
        }}
      />

      {/* ---- Colours ---- */}
      <Card variant="outlined">
        <CardHeader title={<SectionTitle icon={<Palette size={18} />}>Colours</SectionTitle>} />
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {SPOTLIGHT_PALETTES.map((p) => (
              <Button
                key={p.id}
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => update({ colors: { ...p.colors } })}
                data-testid={`spotlight-palette-${p.id}`}
                startIcon={
                  <Box sx={{ display: "flex" }}>
                    {[p.colors.accent, p.colors.button].map((hex, i) => (
                      <Box key={i} sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: hex, border: "1px solid", borderColor: "divider", ml: -0.25 }} />
                    ))}
                  </Box>
                }
              >
                {p.name}
              </Button>
            ))}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
            {COLOR_FIELDS.map(({ key, label }) => {
              const value = c.colors[key]
              const valid = safeColor(value, "") !== ""
              return (
                <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component="input"
                    type="color"
                    aria-label={`${label} colour`}
                    value={valid && value.length === 7 ? value : DEFAULT_SPOTLIGHT_COLORS[key]}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateColors({ [key]: e.target.value })}
                    sx={{ width: 36, height: 36, p: 0, border: "1px solid", borderColor: "divider", borderRadius: 1, cursor: "pointer", bgcolor: "transparent" }}
                  />
                  <TextField
                    size="small"
                    label={label}
                    value={value}
                    onChange={(e) => updateColors({ [key]: e.target.value.trim() })}
                    error={!valid}
                    helperText={valid ? undefined : "Use a hex colour like #2a3e6a"}
                    inputProps={{ "data-testid": `spotlight-color-${key}` }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              )
            })}
          </Box>
        </CardContent>
      </Card>

      {/* ---- Logo + banner ---- */}
      <Card variant="outlined">
        <CardHeader title={<SectionTitle icon={<ImageIcon size={18} />}>Logo &amp; banner</SectionTitle>} />
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
          <Box>
            <Typography component="label" sx={labelSx}>Logo</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Box
                component="img"
                src={toPreviewImageUrl(c.logoUrl || XOP_EMAIL_LOGO_URL)}
                alt="Logo"
                data-testid="spotlight-logo-preview"
                sx={{ height: 40, width: "auto", maxWidth: 200, objectFit: "contain", p: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 0.5 }}
              />
              {uploadButton("logo", c.logoUrl ? "Replace logo" : "Upload a different logo")}
              {c.logoUrl && (
                <Button variant="text" size="small" onClick={() => update({ logoUrl: "" })}>
                  Use the XOP logo
                </Button>
              )}
            </Box>
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>Banner</Typography>
            {c.bannerUrl ? (
              <Box
                component="img"
                src={toPreviewImageUrl(c.bannerUrl) + "?w=600&h=240&fit=crop"}
                alt="Banner"
                sx={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 1, mb: 1.5, display: "block" }}
              />
            ) : (
              <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>No banner — the email starts with the title.</Typography>
            )}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, mb: 1.5 }}>
              {[...spotlightBannerOptions, ...customImages].map((img) => (
                <Box
                  component="img"
                  key={img.id}
                  src={toPreviewImageUrl(img.url) + "?w=150&h=100&fit=crop"}
                  alt={img.name}
                  onClick={() => update({ bannerUrl: img.url })}
                  sx={{
                    width: "100%",
                    height: 56,
                    objectFit: "cover",
                    borderRadius: 1,
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: c.bannerUrl === img.url ? accentPreview : "divider",
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {uploadButton("banner", "Upload your own banner")}
              {c.bannerUrl && (
                <Button variant="text" color="error" size="small" onClick={() => update({ bannerUrl: "" })}>
                  Remove banner
                </Button>
              )}
            </Box>
            <Typography sx={hintSx}>Any shape works (it scales to the email's width). Keep it SHORT: 1200 × 400 (3:1) is ideal — a tall banner pushes the title below the fold.</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* ---- Title + register ---- */}
      <Card variant="outlined">
        <CardHeader title={<SectionTitle icon={<Type size={18} />}>Title &amp; registration</SectionTitle>} />
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
          <Box>
            <Typography component="label" sx={labelSx}>Line above the title</Typography>
            <TextField fullWidth size="small" value={c.eyebrow} onChange={(e) => update({ eyebrow: e.target.value })} placeholder="XOP Networks & Partner Joint Webinar | September 30, 2026" />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Title</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="webinar invite headline — a concise, compelling title"
                  placeholder="e.g., mass notification for emergency response"
                  currentValue={c.title}
                  onGenerate={(v) => update({ title: v })}
                />
                <AiTextImprover fieldType="short" context="webinar invite headline" currentText={c.title} onImprove={(v) => update({ title: v })} />
              </Box>
            </Box>
            <TextField fullWidth size="small" value={c.title} onChange={(e) => update({ title: e.target.value })} inputProps={{ "data-testid": "spotlight-title" }} />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>Second line (shown in the accent colour)</Typography>
            <TextField fullWidth size="small" value={c.subtitle} onChange={(e) => update({ subtitle: e.target.value })} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" }, gap: 1.5 }}>
            <Box>
              <Typography component="label" sx={labelSx}>Button text</Typography>
              <TextField fullWidth size="small" value={c.buttonLabel} onChange={(e) => update({ buttonLabel: e.target.value })} />
            </Box>
            <Box>
              <Typography component="label" sx={labelSx}>
                Registration link <Box component="span" sx={{ color: "error.main" }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                required
                value={c.registrationLink}
                onChange={(e) => update({ registrationLink: e.target.value })}
                placeholder="https://…"
                error={!c.registrationLink.trim()}
                helperText={
                  !c.registrationLink.trim()
                    ? "Required to send — both buttons need a destination."
                    : "Used by both buttons."
                }
                inputProps={{ "data-testid": "spotlight-registration-link" }}
              />
            </Box>
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>Inbox preview text</Typography>
            <TextField fullWidth size="small" value={c.preheader} onChange={(e) => update({ preheader: e.target.value })} />
            <Typography sx={hintSx}>Shown next to the subject in the inbox, not in the email itself.</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* ---- Intro + topics ---- */}
      <Card variant="outlined">
        <CardHeader title={<SectionTitle icon={<ListChecks size={18} />}>Invitation text</SectionTitle>} />
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
          <Box>
            <Typography component="label" sx={labelSx}>Greeting</Typography>
            <TextField fullWidth size="small" value={c.greeting} onChange={(e) => update({ greeting: e.target.value })} />
            <Typography sx={hintSx}>
              <code>{"{{first_name}}"}</code> is replaced with each recipient&apos;s first name when the email is sent.
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Introduction</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="long"
                  context="webinar invite introduction — the problem, who presents, and why attend"
                  placeholder="e.g., the challenge and who is presenting"
                  currentValue={plainText(c.intro)}
                  onGenerate={(v) => update({ intro: `<p>${v}</p>` })}
                />
                <AiTextImprover fieldType="long" context="webinar invite introduction" currentText={plainText(c.intro)} onImprove={(v) => update({ intro: `<p>${v}</p>` })} />
              </Box>
            </Box>
            <TipTapEditor value={c.intro} onChange={(html) => update({ intro: html })} placeholder="Introduce the webinar…" />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>Topics heading</Typography>
            <TextField fullWidth size="small" value={c.coverHeading} onChange={(e) => update({ coverHeading: e.target.value })} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {c.coverItems.map((item, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  size="small"
                  value={item}
                  onChange={(e) => update({ coverItems: c.coverItems.map((v, j) => (j === i ? e.target.value : v)) })}
                  inputProps={{ "data-testid": `spotlight-topic-${i}`, "aria-label": `Topic ${i + 1}` }}
                />
                <Tooltip title="Remove topic">
                  <IconButton size="small" aria-label={`Remove topic ${i + 1}`} onClick={() => update({ coverItems: c.coverItems.filter((_, j) => j !== i) })}>
                    <Trash2 size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Box>
              <Button size="small" startIcon={<Plus size={16} />} onClick={() => update({ coverItems: [...c.coverItems, ""] })}>
                Add topic
              </Button>
            </Box>
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>Closing line (bold)</Typography>
            <TextField fullWidth size="small" value={c.closingLine} onChange={(e) => update({ closingLine: e.target.value })} />
            <Typography sx={hintSx}>Leave blank to hide it.</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* ---- When ---- */}
      <Card variant="outlined">
        <CardHeader title={<SectionTitle icon={<Calendar size={18} />}>When</SectionTitle>} />
        <CardContent sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 3fr" }, gap: 1.5, pt: 0 }}>
          <Box>
            <Typography component="label" sx={labelSx}>Heading</Typography>
            <TextField fullWidth size="small" value={c.whenHeading} onChange={(e) => update({ whenHeading: e.target.value })} />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>Date and time</Typography>
            <TextField fullWidth size="small" value={c.when} onChange={(e) => update({ when: e.target.value })} placeholder="September 30, 2026 | 11:00 AM AST (8:00 AM GMT)" />
          </Box>
        </CardContent>
      </Card>

      {/* ---- Speakers ---- */}
      <Card variant="outlined">
        <CardHeader title={<SectionTitle icon={<Users size={18} />}>Speakers</SectionTitle>} />
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
          <Box>
            <Typography component="label" sx={labelSx}>Heading</Typography>
            <TextField fullWidth size="small" value={c.speakersHeading} onChange={(e) => update({ speakersHeading: e.target.value })} />
          </Box>
          {c.speakers.map((s, i) => (
            <Box key={s.id} sx={{ display: "flex", gap: 2, alignItems: "flex-start", p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }} data-testid={`spotlight-speaker-${i}`}>
              {s.photoUrl ? (
                <Box component="img" src={toPreviewImageUrl(s.photoUrl)} alt={s.name} sx={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
              ) : (
                <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: accentPreview, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>
                  {initials(s.name)}
                </Box>
              )}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField size="small" label="Name" value={s.name} onChange={(e) => updateSpeaker(s.id, { name: e.target.value })} />
                <TextField
                  size="small"
                  label="Title and company"
                  multiline
                  minRows={2}
                  value={s.title}
                  onChange={(e) => updateSpeaker(s.id, { title: e.target.value })}
                  helperText="Each line shows on its own line."
                />
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {uploadButton(s.id, s.photoUrl ? "Replace photo" : "Upload photo")}
                  {s.photoUrl && (
                    <Button variant="text" size="small" onClick={() => updateSpeaker(s.id, { photoUrl: "" })}>
                      Remove photo
                    </Button>
                  )}
                </Box>
              </Box>
              <Tooltip title="Remove speaker">
                <IconButton size="small" aria-label={`Remove speaker ${i + 1}`} onClick={() => update({ speakers: c.speakers.filter((x) => x.id !== s.id) })}>
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<Plus size={16} />}
              disabled={c.speakers.length >= SPOTLIGHT_MAX_SPEAKERS}
              onClick={() => update({ speakers: [...c.speakers, { id: newSpeakerId(), name: "", title: "", photoUrl: "" }] })}
            >
              Add speaker
            </Button>
            <Typography sx={hintSx}>
              Up to {SPOTLIGHT_MAX_SPEAKERS}, shown side by side. With no speakers the section is hidden. Headshots are cropped to a square automatically on upload and shown as a circle.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            The footer shows the {tenantName} address, website and the company LinkedIn + YouTube links automatically.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}

export default SpotlightWebinarBuilder
